import type { Locale } from "./i18n";
import type { LauncherAnchor } from "./launcher-anchor";
import type { FabPosition } from "./types";

import { glyphOf } from "./glyphs";
import {
	setLanguage,
	statusHint,
	statusLabel,
	t,
	timeframeLabel,
	tWith,
} from "./i18n";
import { applyAnchor } from "./launcher-anchor";
import previewCss from "./preview.css?inline";
import { attachStyles } from "./styles";

const HOST_ID = "weloop-preview";

// Must match the `furl` keyframe duration in preview.css – the phase flips to
// "hidden" when the pill has finished travelling into the launcher.
const FURL_MS = 340;

// How long the pill peeks before it furls into the launcher badge.
const PEEK_MS = 8000;

/** One unread conversation, as the `weloop:unread` message carries it. */
export interface UnreadItem {
	/** Identity fallback when the actor never set a display name. */
	readonly actorEmail: string | null;
	readonly actorName: string | null;
	readonly createdAt: string;
	readonly excerpt: string | null;
	readonly feedbackId: string;
	/** Unread notifications in this conversation – shows "+N" past one. */
	readonly itemCount: number;
	/**
	 * Notification kind – picks the headline wording and the row accent. Null
	 * on stale widgets that predate the field.
	 */
	readonly kind: string | null;
	/** Classification key of the thread's business rule – icon and tint. */
	readonly ruleClassification: string | null;
	/** Title of the matching business rule, shown as the pill's second row. */
	readonly ruleTitle: string | null;
	/** New thread status when `kind` is `feedback_status_change`. */
	readonly status: string | null;
	/** The conversation's own label, when the thread has a summary. */
	readonly threadLabel: string | null;
	/** New expected timeframe when `kind` is `feedback_expected_timeframe_change`. */
	readonly timeframe: string | null;
}

// The status itself is the information on a status change – "moved to Done"
// helps, "bruce.wayne" alone misleads. Unknown kinds (a newer server than
// this bundle) keep the actor-identity headline, which is true for any
// activity.
export const headline = (locale: Locale, data: UnreadItem): string => {
	if (data.kind === "feedback_status_change") {
		return data.status === null
			? t(locale, "preview_status_generic")
			: tWith(locale, "preview_status_change", {
					status: statusLabel(locale, data.status),
				});
	}
	if (data.kind === "feedback_expected_timeframe_change") {
		const label =
			data.timeframe === null ? undefined : timeframeLabel(data.timeframe);
		return label === undefined
			? t(locale, "preview_timeframe_change")
			: tWith(locale, "preview_timeframe_change_to", { timeframe: label });
	}
	if (data.kind === "feedback_confirmation_request") {
		return t(locale, "preview_confirmation_request");
	}
	return data.actorName ?? data.actorEmail ?? t(locale, "preview_reply");
};

/**
 * The second line of a card row or the pill. A reply quotes itself; a
 * status change has nothing to quote, so it explains what the status means
 * for the person who asked ("Your request was closed without a fix.").
 */
export const excerptOf = (data: UnreadItem): string => {
	if (data.excerpt !== null && data.excerpt !== "") {
		return data.excerpt;
	}
	if (data.kind === "feedback_status_change" && data.status !== null) {
		return statusHint(data.status) ?? "";
	}
	return "";
};

// The team's "does the fix work?" ask: not a reply to read but a question
// to answer, so the pill leads with the question rather than the asker.
export const isAskKind = (kind: string | null): boolean =>
	kind === "feedback_confirmation_request";

// Progress events – a status or timeframe change – are not replies: they
// share the kind-aware headline and the status tint.
export const isProgressKind = (kind: string | null): boolean =>
	kind === "feedback_status_change" ||
	kind === "feedback_expected_timeframe_change";

export interface PreviewHandle {
	readonly dispose: () => void;
	readonly hide: () => void;
	/**
	 * Re-pin the pill next to the launcher. Called before every `show` – the
	 * launcher moves (chat-head drag, tab modes), and the pill must peek out
	 * of wherever it actually sits, not the configured corner.
	 */
	readonly setAnchor: (anchor: LauncherAnchor) => void;
	/** See `LauncherFollower.setLauncherMoving`. */
	readonly setLauncherMoving: (moving: boolean) => void;
	/**
	 * `total` is every unread conversation, `items` only the ones the card
	 * lists – the widget caps the list, so the two diverge for a busy inbox
	 * and the counts must come from `total`.
	 */
	readonly show: (items: readonly UnreadItem[], total: number) => void;
}

// Read by code point, not by UTF-16 unit, so a name starting outside the
// basic plane yields one character instead of half a surrogate pair.
const firstLetterOf = (word: string): string => {
	const code = word.codePointAt(0);
	return code === undefined ? "" : String.fromCodePoint(code).toUpperCase();
};

// "Marie Dupont" -> "MD". One letter is enough for a mononym; anything past
// two is noise at 28px.
const initialsOf = (name: string | null): string => {
	if (name === null || name.trim() === "") {
		return "";
	}
	return name
		.trim()
		.split(/\s+/u)
		.slice(0, 2)
		.map((word) => firstLetterOf(word))
		.join("");
};

const firstNameOf = (name: string): string =>
	name.trim().split(/\s+/u)[0] ?? "";

// Who acted, as the pill shows it: the first name when the actor has a
// display name, their email otherwise.
const actorLabelOf = (item: UnreadItem): string | null =>
	item.actorName === null ? item.actorEmail : firstNameOf(item.actorName);

// Status changes read as progress, new messages as conversation – the avatar
// tint is the only thing that separates them in the list.
const toneOf = (kind: string | null): string =>
	isProgressKind(kind) ? "status" : "reply";

// Classification → glyph kind, mirroring the UI package's
// `ClassificationIcon` and the dashboard's `clsChipIcon` tones so the card and /rules always agree on
// which icon and tint a classification wears.
const CLASSIFICATION_KINDS: Readonly<Record<string, string>> = {
	bug_report: "bug",
	business_request: "business",
	business_support: "support",
	feature_request: "feature",
	it_issue: "it",
};

const classKindOf = (item: UnreadItem): string | undefined =>
	item.ruleClassification === null
		? undefined
		: CLASSIFICATION_KINDS[item.ruleClassification];

// The disc next to the headline: the rule's classification glyph when the
// thread is classified, the actor's initials otherwise, hidden when neither
// leaves anything to draw.
// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- HTMLElement is an external mutable DOM interface
const renderDisc = (disc: HTMLElement, item: UnreadItem): void => {
	const kind = classKindOf(item);
	if (kind !== undefined) {
		disc.dataset["kind"] = kind;
		disc.replaceChildren(glyphOf(kind));
		disc.style.display = "";
		return;
	}
	delete disc.dataset["kind"];
	disc.dataset["tone"] = toneOf(item.kind);
	disc.textContent = initialsOf(item.actorName) || initialsOf(item.threadLabel);
	disc.style.display = disc.textContent === "" ? "none" : "";
};

/**
 * Unread notifications surfaced next to the launcher while the widget is
 * closed.
 *
 * Think of it as a train pulling into a station: the pill peeks out for a few
 * seconds with a hairline timer draining along its top edge, then furls back
 * into the launcher, leaving the count badge behind. Nothing is marked read on
 * the way – a missed support reply is costly, so the badge stays lit until the
 * user acts. Hovering or activating the pill expands it into the unread list,
 * where the user can open one conversation, open the inbox, or mark the lot
 * read.
 *
 * Closed shadow root, like the FAB: host-page code sees only the wrapper.
 */
export const mountPreview = (
	locale: Locale,
	position: FabPosition,
	callbacks: {
		/**
		 * The pill was waved away – "Later", or the peek timer ran out. Either
		 * way the reply was on screen, so remember it: only the launcher badge
		 * keeps announcing it, on this and future page loads.
		 */
		readonly onDismiss: () => void;
		/** Escape closed the pill or the card – focus goes back to the launcher. */
		readonly onEscape: () => void;
		readonly onMarkRead: () => void;
		readonly onOpen: (feedbackId: string) => void;
		readonly onOpenInbox: () => void;
	},
): PreviewHandle => {
	const host = document.createElement("div");
	host.id = HOST_ID;
	host.dataset["position"] = position;
	host.dataset["phase"] = "hidden";
	const shadow = host.attachShadow({ mode: "closed" });

	attachStyles(shadow, previewCss);

	let items: readonly UnreadItem[] = [];
	let total = 0;
	// Timestamp the pill last peeked for. A poll that repeats the same state
	// must not re-arm the peek; a newer reply carries a newer timestamp and
	// does.
	let armedAt: string | undefined = undefined;
	let furlTimer: ReturnType<typeof setTimeout> | undefined;
	let peekTimer: ReturnType<typeof setTimeout> | undefined;
	// A reply that arrived while the tab was in the background. It peeks once
	// the visitor comes back – the timer would otherwise run out, and record
	// the reply as seen, with nobody looking.
	let isPeekDue = false;

	// --- the peek pill ------------------------------------------------------
	const pill = document.createElement("div");
	pill.className = "pill";
	pill.setAttribute("role", "button");
	pill.tabIndex = 0;

	// The pill is named by its visible words – "Marie replied", the excerpt –
	// so what a screen reader says matches what a sighted user reads. The
	// initials say nothing on their own.
	const pillAvatar = document.createElement("span");
	pillAvatar.className = "avatar";
	pillAvatar.setAttribute("aria-hidden", "true");

	const pillName = document.createElement("span");
	pillName.className = "name";

	const pillExcerpt = document.createElement("span");
	pillExcerpt.className = "excerpt";

	// Two stacked lines beside the disc: who acted + what they said, then the
	// matched rule's title in the classification tint.
	const pillLine = document.createElement("span");
	pillLine.className = "line";
	pillLine.append(pillName, pillExcerpt);

	const pillTitle = document.createElement("span");
	pillTitle.className = "ruleTitle";

	const pillMain = document.createElement("span");
	pillMain.className = "main";
	pillMain.append(pillLine, pillTitle);

	const pillMore = document.createElement("span");
	pillMore.className = "more";
	pillMore.title = t(locale, "preview_more");

	const timer = document.createElement("span");
	timer.className = "timer";

	pill.append(pillAvatar, pillMain, pillMore, timer);

	// Polite live region for the headline. It stays in the page while the pill
	// is hidden – a region must exist before its text changes to be announced.
	const live = document.createElement("span");
	live.className = "live";
	live.setAttribute("role", "status");

	// --- the expanded card --------------------------------------------------
	const card = document.createElement("div");
	card.className = "card";
	card.setAttribute("role", "dialog");

	const head = document.createElement("div");
	head.className = "head";

	const eyebrow = document.createElement("span");
	eyebrow.className = "eyebrow";

	const later = document.createElement("button");
	later.className = "ghost";
	later.type = "button";
	later.textContent = t(locale, "preview_later");

	head.append(eyebrow, later);

	const list = document.createElement("div");
	list.className = "list";

	const foot = document.createElement("div");
	foot.className = "foot";

	const openInbox = document.createElement("button");
	openInbox.className = "primary";
	openInbox.type = "button";
	openInbox.textContent = t(locale, "preview_open_inbox");

	const markRead = document.createElement("button");
	markRead.className = "outline";
	markRead.type = "button";
	markRead.textContent = t(locale, "preview_mark_read");

	foot.append(openInbox, markRead);
	card.append(head, list, foot);

	shadow.append(pill, card, live);
	document.body.append(host);

	const clearTimers = (): void => {
		if (peekTimer !== undefined) {
			clearTimeout(peekTimer);
			peekTimer = undefined;
		}
		if (furlTimer !== undefined) {
			clearTimeout(furlTimer);
			furlTimer = undefined;
		}
	};

	const setPhase = (phase: string): void => {
		host.dataset["phase"] = phase;
	};

	const collapse = (): void => {
		clearTimers();
		isPeekDue = false;
		live.textContent = "";
		setPhase("hidden");
	};

	// `focusInside` for a keyboard or tap activation: the pill that held focus
	// is hidden by the expansion, so focus moves to the first row instead of
	// falling to the page body.
	const expand = (focusInside: boolean): void => {
		if (items.length === 0) {
			return;
		}
		clearTimers();
		setPhase("expanded");
		if (focusInside) {
			(list.querySelector("button") ?? openInbox).focus({
				preventScroll: true,
			});
		}
	};

	// Restarting a CSS animation needs the old one torn off and the layout
	// flushed before the new one is attached.
	const restartTimerBar = (): void => {
		timer.style.animation = "none";
		void timer.offsetWidth;
		timer.style.animation = "";
	};

	// The countdown alone, without the entrance – focus on the pill stops it,
	// and leaving the pill starts it over.
	const startPeekTimer = (): void => {
		clearTimers();
		restartTimerBar();
		peekTimer = setTimeout(() => {
			// The timer ran out with the reply on screen – that showing was its
			// one turn. Recording it as dismissed keeps the same reply from
			// peeking again on the next poll or a reload; the launcher badge
			// stays lit either way.
			callbacks.onDismiss();
			setPhase("furling");
			furlTimer = setTimeout(() => {
				setPhase("hidden");
			}, FURL_MS);
		}, PEEK_MS);
	};

	const peek = (): void => {
		clearTimers();
		if (document.visibilityState === "hidden") {
			isPeekDue = true;
			return;
		}
		isPeekDue = false;
		setLanguage(host, locale);
		setPhase("pill");
		live.textContent = pillName.textContent;
		startPeekTimer();
	};

	const onVisibilityChange = (): void => {
		if (isPeekDue && document.visibilityState === "visible") {
			peek();
		}
	};
	document.addEventListener("visibilitychange", onVisibilityChange);

	const renderPill = (item: UnreadItem): void => {
		renderDisc(pillAvatar, item);
		const actorLabel = actorLabelOf(item);
		// A progress event is not a reply, so it borrows the shared kind-aware
		// wording; an actual reply keeps the friendlier "<First> replied".
		pillName.textContent =
			isProgressKind(item.kind) || isAskKind(item.kind) || actorLabel === null
				? headline(locale, item)
				: tWith(locale, "preview_replied", { name: actorLabel });
		pillExcerpt.textContent = excerptOf(item);
		pillTitle.textContent = item.ruleTitle ?? "";
		const kind = classKindOf(item);
		if (kind === undefined) {
			delete pillTitle.dataset["kind"];
		} else {
			pillTitle.dataset["kind"] = kind;
		}
		pillTitle.style.display = item.ruleTitle === null ? "none" : "";
		// Everything still waiting behind the reply on show: the other unread
		// conversations, plus the earlier replies in this one.
		const extra = total - 1 + (item.itemCount - 1);
		pillMore.textContent = extra > 0 ? `+${extra}` : "";
		pillMore.style.display = extra > 0 ? "" : "none";
	};

	const renderRow = (item: UnreadItem): HTMLButtonElement => {
		const row = document.createElement("button");
		row.className = "row";
		row.type = "button";

		// The classification glyph when the thread is classified; otherwise the
		// author's initials, falling back to the thread's own label rather than
		// rendering an empty disc.
		const avatar = document.createElement("span");
		avatar.className = "avatar";
		renderDisc(avatar, item);

		const body = document.createElement("span");
		body.className = "body";

		const title = document.createElement("span");
		title.className = "title";
		// "Marie Dupont · Export PDF bug", or "Moved to Done · Export PDF bug"
		// for a status change – the headline already words each kind honestly.
		title.textContent = [headline(locale, item), item.threadLabel]
			.filter((part) => part !== null && part !== "")
			.join(" · ");

		const excerpt = document.createElement("span");
		excerpt.className = "excerpt";
		excerpt.textContent = excerptOf(item);

		body.append(title, excerpt);
		if (avatar.style.display !== "none") {
			row.append(avatar);
		}
		row.append(body);
		if (item.itemCount > 1) {
			const more = document.createElement("span");
			more.className = "more";
			more.title = t(locale, "preview_more");
			more.textContent = `+${item.itemCount - 1}`;
			row.append(more);
		}
		row.addEventListener("click", () => {
			callbacks.onOpen(item.feedbackId);
		});
		return row;
	};

	const renderCard = (): void => {
		eyebrow.textContent = tWith(locale, "unread", { count: String(total) });
		card.setAttribute("aria-label", eyebrow.textContent);
		list.replaceChildren(...items.map((item) => renderRow(item)));
	};

	pill.addEventListener("mouseenter", () => {
		expand(false);
	});
	pill.addEventListener("click", () => {
		expand(true);
	});
	pill.addEventListener("keydown", (event) => {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			expand(true);
		}
	});
	// Reading the pill with a keyboard or a screen reader takes longer than
	// the peek – the countdown waits while focus is on it.
	// The drain bar pauses with it, in preview.css.
	pill.addEventListener("focusin", () => {
		if (host.dataset["phase"] === "pill") {
			clearTimers();
		}
	});
	pill.addEventListener("focusout", () => {
		if (host.dataset["phase"] === "pill") {
			startPeekTimer();
		}
	});
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- KeyboardEvent is an external mutable DOM interface
	const onEscapeKey = (event: KeyboardEvent): void => {
		if (event.key === "Escape") {
			collapse();
			callbacks.onEscape();
		}
	};
	pill.addEventListener("keydown", onEscapeKey);
	card.addEventListener("keydown", onEscapeKey);
	// Leaving the card collapses it – the user looked and moved on. The badge
	// keeps the count, so nothing is lost.
	card.addEventListener("mouseleave", collapse);
	later.addEventListener("click", () => {
		collapse();
		callbacks.onDismiss();
	});
	openInbox.addEventListener("click", () => {
		collapse();
		callbacks.onOpenInbox();
	});
	markRead.addEventListener("click", () => {
		collapse();
		callbacks.onMarkRead();
	});

	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- PointerEvent is an external mutable DOM interface
	const onDocumentPointerDown = (event: PointerEvent): void => {
		// `composedPath` sees through the closed shadow root, so a click on the
		// card's own buttons is correctly read as inside.
		if (
			host.dataset["phase"] === "expanded" &&
			!event.composedPath().includes(host)
		) {
			collapse();
		}
	};
	document.addEventListener("pointerdown", onDocumentPointerDown, true);

	return {
		dispose: () => {
			clearTimers();
			document.removeEventListener("pointerdown", onDocumentPointerDown, true);
			document.removeEventListener("visibilitychange", onVisibilityChange);
			host.remove();
		},
		// Yielding the corner to the drawer or a survey popup. The arm is kept,
		// so a reply already peeked at does not peek a second time when the
		// higher surface leaves – one that never got its turn still will,
		// because `show` was never reached for it.
		hide: collapse,
		setAnchor: (anchor) => {
			// data-position keeps the furl direction and the timer's drain
			// origin pointing at the launcher.
			applyAnchor(host, anchor, anchor.side);
		},
		setLauncherMoving: (moving) => {
			host.toggleAttribute("data-launcher-moving", moving);
		},
		show: (next, nextTotal) => {
			if (next.length === 0) {
				items = [];
				total = 0;
				armedAt = undefined;
				collapse();
				return;
			}
			items = next;
			// A count below the listed rows would be a stale or malformed
			// message – the rows on screen are the floor.
			total = Math.max(nextTotal, next.length);
			const [latest] = next;
			if (latest === undefined) {
				return;
			}
			renderPill(latest);
			renderCard();
			// Only a reply the user has not been shown yet re-opens the peek.
			// Repeat polls keep the card contents fresh without stealing focus
			// back from whatever the user is doing.
			if (armedAt !== latest.createdAt) {
				armedAt = latest.createdAt;
				peek();
			}
		},
	};
};
